import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, tokenStore } from "../lib/adminApi";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import {
  LogOut, BedDouble, Bell, Download, Minus, Plus, RefreshCw, Mail, Phone, MessageSquare, Calendar,
  ChevronLeft, ChevronRight, Globe, X,
} from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WD = ["S", "M", "T", "W", "T", "F", "S"];

const ROOM_LABELS = {
  "double-fan": "Double Standard · Fan",
  "double-ac": "Double Standard · Air Conditioning",
  "double-ac-2": "AC + Shared Balcony",
  triple: "Triple Room",
  quadruple: "Quadruple Room",
};

const ROOM_SHORT = {
  "double-fan": "Fan",
  "double-ac": "AC",
  "double-ac-2": "AC2",
  triple: "Triple",
  quadruple: "Quad",
};

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WD_ES = ["D", "L", "M", "X", "J", "V", "S"];

const ROOM_UNIT_TOTALS = {
  "double-fan": 2,
  "double-ac": 2,
  "double-ac-2": 2,
  triple: 1,
  quadruple: 1,
};

const ROOM_LABELS_ES = {
  "double-fan": "Doble estándar · Ventilador",
  "double-ac": "Doble estándar · Aire acondicionado",
  "double-ac-2": "Aire acond. + balcón compartido",
  triple: "Habitación triple",
  quadruple: "Habitación cuádruple",
};

const adminRoomLabel = (tr, id) => tr(ROOM_LABELS[id] || id, ROOM_LABELS_ES[id] || id);

const AVAILABILITY_STYLES = {
  open: { label: "Open", className: "text-emerald-200", dot: "bg-emerald-300", active: "bg-emerald-400 text-purpleink-deep" },
  closed: { label: "Closed", className: "text-red-200", dot: "bg-red-300", active: "bg-red-400 text-purpleink-deep" },
  booked: { label: "Booked", className: "text-sky-200", dot: "bg-sky-300", active: "bg-sky-400 text-purpleink-deep" },
  mixed: { label: "Mixed", className: "text-marigold", dot: "bg-marigold", active: "bg-marigold text-purpleink-deep" },
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}


const makeAdminTranslator = (lang) => (en, es) => (lang === "es" ? es : en);
const Login = ({ onLogin, lang, setLang }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const tr = makeAdminTranslator(lang);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { token, user } = await api.login(email.trim().toLowerCase(), password.trim());
      tokenStore.set(token);
      onLogin(user);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/40 focus:border-marigold focus:ring-2 focus:ring-marigold/30";

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-purpleink-deep px-4 grain">
      <button onClick={() => setLang(lang === "es" ? "en" : "es")} data-testid="admin-language-toggle" className="absolute right-4 top-4 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs font-bold text-white/70 hover:bg-white/10">
        {lang === "es" ? "ES" : "EN"}
      </button>
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl"
      >
        <div className="mb-6 text-center">
          <img src="/logo.png" alt="Casa Romano" className="mx-auto mb-3 h-14 w-14 rounded-full object-cover" />
          <h1 className="font-display text-2xl font-bold text-white">Casa Romano Admin</h1>
          <p className="mt-1 text-sm text-white/50">{tr("Sign in to manage your hostel", "Inicia sesión para administrar tu hostal")}</p>
        </div>
        <div className="space-y-3">
          <input data-testid="admin-email" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={tr("Email", "Correo electrónico")} value={email} onChange={(e) => setEmail(e.target.value)} className={input} autoComplete="username" />
          <input data-testid="admin-password" type="password" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={tr("Password", "Contraseña")} value={password} onChange={(e) => setPassword(e.target.value)} className={input} autoComplete="current-password" />
        </div>
        {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-300">{error}</p>}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-6 w-full rounded-full bg-marigold px-6 py-3 text-base font-semibold text-purpleink-deep transition-all hover:brightness-105 active:scale-95 disabled:opacity-60"
        >
          {loading ? tr("Signing in…", "Iniciando sesión…") : tr("Sign in", "Iniciar sesión")}
        </button>
      </form>
    </div>
  );
};


const DayDetailsModal = ({
  day,
  rooms,
  roomStatus,
  entriesFor,
  effectivePrice,
  basePrice,
  rate,
  updating,
  onSavePrice,
  onResetPrice,
  onSetStatus,
  onClose,
  tr,
}) => {
  const [popupScope, setPopupScope] = useState("day");
  const [bulkPrice, setBulkPrice] = useState("");
  const [draftPrices, setDraftPrices] = useState(() => Object.fromEntries(rooms.map((room) => [room.id, effectivePrice(day, room.id)])));
  if (!day) return null;
  const setDraft = (roomId, value) => setDraftPrices((drafts) => ({ ...drafts, [roomId]: value }));
  const cordoba = (value) => `C$${Math.round(Number(value || 0) * Number(rate || 0)).toLocaleString()}`;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 backdrop-blur-sm sm:items-center" data-testid="day-details-modal">
      <div className="max-h-[86vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-white/10 bg-purpleink-deep p-4 shadow-2xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-white/45">{tr("Room availability", "Disponibilidad de habitaciones")}</p>
            <h3 className="mt-1 font-display text-xl font-bold" data-testid="day-details-date">{day}</h3>
          </div>
          <button onClick={onClose} data-testid="day-details-close" aria-label={tr("Close day details", "Cerrar detalles del día")} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-white/20">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-3 rounded-2xl border border-white/10 bg-white/[0.03] p-2">
          <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-wide text-white/45">{tr("Apply edits to", "Aplicar cambios a")}</p>
          <div className="grid grid-cols-3 gap-1 rounded-full bg-white/5 p-1 text-xs">
            {[ ["day", tr("Day", "Día")], ["week", tr("Week", "Semana")], ["month", tr("Month", "Mes")] ].map(([value, label]) => (
              <button key={value} onClick={() => setPopupScope(value)} data-testid={`popup-scope-${value}`} className={`rounded-full px-2 py-2 font-semibold ${popupScope === value ? "bg-marigold text-purpleink-deep" : "text-white/60"}`}>{label}</button>
            ))}
          </div>
        </div>

        <div className="mb-3 rounded-2xl border border-marigold/25 bg-marigold/10 p-3" data-testid="popup-all-rooms-editor">
          <p className="text-xs font-semibold uppercase tracking-wide text-marigold">{tr("All rooms", "Todas las habitaciones")}</p>
          <p className="mt-1 text-xs text-white/55">{tr("Set one price or status for every room in the selected scope.", "Aplica un precio o estado a todas las habitaciones en el alcance seleccionado.")}</p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
            <input
              type="number"
              min="0"
              step="1"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              placeholder={tr("Price for all", "Precio para todas")}
              data-testid="popup-all-price-input"
              className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white"
            />
            <button onClick={() => onSavePrice("all", Number(bulkPrice), popupScope)} disabled={updating || bulkPrice === ""} data-testid="popup-all-price-save" className="rounded-full bg-marigold px-3 py-2 text-xs font-bold text-purpleink-deep disabled:opacity-40">{tr("Save", "Guardar")}</button>
            <button onClick={() => onResetPrice("all", popupScope)} disabled={updating} data-testid="popup-all-price-base" className="rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-white/70 disabled:opacity-40">{tr("Base", "Base")}</button>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-1 rounded-full bg-white/5 p-1 text-[0.68rem]">
            {[0, 1, 2].map((count) => (
              <button key={count} onClick={() => onSetStatus("all", count === 0 ? "open" : "booked", popupScope, count)} disabled={updating} data-testid={`popup-all-booked-count-${count}`} className="rounded-full px-2 py-1.5 font-semibold text-white/70 hover:bg-white/10 disabled:opacity-40">
                {tr(`${count} booked`, `${count} reservadas`)}
              </button>
            ))}
            <button onClick={() => onSetStatus("all", "closed", popupScope)} disabled={updating} data-testid="popup-all-set-closed" className="rounded-full px-2 py-1.5 font-semibold text-white/70 hover:bg-white/10 disabled:opacity-40">{tr("Closed", "Cerrado")}</button>
          </div>
        </div>

        <div className="space-y-3">
          {rooms.map((room) => {
            const status = roomStatus(day, room.id);
            const entries = entriesFor(day, room.id).filter((entry) => entry.guest_name || entry.note);
            const draft = draftPrices[room.id] ?? effectivePrice(day, room.id);
            return (
              <div key={room.id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3" data-testid={`day-detail-${room.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{adminRoomLabel(tr, room.id)}</p>
                    <p className={`mt-1 text-xs font-bold ${status.className}`} data-testid={`day-detail-status-${room.id}`}>{status.label}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-white" data-testid={`day-detail-count-${room.id}`}>{status.available}/{status.total} {tr("available", "disponibles")}</p>
                    <p className="text-xs text-marigold">${effectivePrice(day, room.id)}</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1 rounded-2xl bg-white/5 p-1 text-[0.68rem]">
                  {Array.from({ length: (ROOM_UNIT_TOTALS[room.id] ?? 1) + 1 }).map((_, count) => {
                    const active = count === 0 ? status.state === "open" : status.state === "booked" && status.bookedCount === count;
                    return (
                      <button
                        key={count}
                        onClick={() => onSetStatus(room.id, count === 0 ? "open" : "booked", popupScope, count)}
                        disabled={updating}
                        data-testid={`day-detail-${room.id}-booked-count-${count}`}
                        className={`min-w-16 flex-1 rounded-full px-2 py-1.5 font-semibold disabled:opacity-40 ${active ? "bg-sky-400 text-purpleink-deep" : "text-white/60"}`}
                      >
                        {tr(`${count} booked`, `${count} reservadas`)}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => onSetStatus(room.id, "closed", popupScope)}
                    disabled={updating}
                    data-testid={`day-detail-${room.id}-set-closed`}
                    className={`min-w-16 flex-1 rounded-full px-2 py-1.5 font-semibold disabled:opacity-40 ${status.state === "closed" ? "bg-red-400 text-purpleink-deep" : "text-white/60"}`}
                  >
                    {tr("Closed", "Cerrado")}
                  </button>
                </div>

                <div className="mt-3 rounded-2xl bg-black/15 p-2">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-white/45">{tr("Nightly price", "Precio por noche")}</p>
                    <p className="text-xs text-white/50">{cordoba(draft)}</p>
                  </div>
                  <div className="grid grid-cols-[2.2rem_1fr_2.2rem] gap-1">
                    <button onClick={() => setDraft(room.id, Math.max(0, Number(draft || 0) - 1))} data-testid={`day-detail-${room.id}-price-minus`} className="rounded-lg bg-white/10 font-bold hover:bg-white/20">−</button>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={draft}
                      onChange={(e) => setDraft(room.id, e.target.value)}
                      data-testid={`day-detail-${room.id}-price-input`}
                      className="w-full rounded-lg border border-white/15 bg-white/5 px-2 py-2 text-center text-sm text-white"
                    />
                    <button onClick={() => setDraft(room.id, Math.min(300, Number(draft || 0) + 1))} data-testid={`day-detail-${room.id}-price-plus`} className="rounded-lg bg-white/10 font-bold hover:bg-white/20">+</button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button onClick={() => onSavePrice(room.id, Number(draft), popupScope)} disabled={updating || draft === ""} data-testid={`day-detail-${room.id}-price-save`} className="rounded-full bg-marigold px-3 py-2 text-xs font-bold text-purpleink-deep hover:brightness-105 disabled:opacity-40">
                      {tr("Save price", "Guardar precio")}
                    </button>
                    <button onClick={() => { setDraft(room.id, basePrice(room.id)); onResetPrice(room.id, popupScope); }} disabled={updating} data-testid={`day-detail-${room.id}-price-base`} className="rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-white/70 hover:bg-white/15 disabled:opacity-40">
                      {tr("Use base", "Usar base")}
                    </button>
                  </div>
                </div>

                {entries.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {entries.map((entry, index) => (
                      <div key={index} className="rounded-xl bg-black/20 p-2" data-testid={`day-detail-${room.id}-guest-${index + 1}`}>
                        <p className="text-xs font-semibold text-sky-200">{tr("Bedroom", "Habitación")} {index + 1}: {entry.guest_name || tr("Guest name not saved", "Nombre del huésped no guardado")}</p>
                        {entry.note && <p className="mt-1 text-xs text-white/55">{entry.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={onClose} data-testid="day-details-edit" className="mt-4 w-full rounded-full bg-marigold px-4 py-3 text-sm font-bold text-purpleink-deep hover:brightness-105">{tr("Done", "Listo")}</button>
      </div>
    </div>
  );
};
const PricesPanel = ({ rooms, onAvailabilityChange, tr }) => {
  const now = new Date();
  const todayISO = iso(now.getFullYear(), now.getMonth(), now.getDate());
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [prices, setPrices] = useState({});
  const [rate, setRate] = useState(36.8);
  const [daily, setDaily] = useState({});
  const [availability, setAvailability] = useState({});
  const [availabilityStatuses, setAvailabilityStatuses] = useState({});
  const [bookedDetails, setBookedDetails] = useState({});
  const [bookedEntryMap, setBookedEntryMap] = useState({});
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [selectedDay, setSelectedDay] = useState(todayISO);
  const [priceValue, setPriceValue] = useState(30);
  const [priceMode, setPriceMode] = useState("keep");
  const [statusChoice, setStatusChoice] = useState("");
  const [bookedGuest, setBookedGuest] = useState("");
  const [bookedNote, setBookedNote] = useState("");
  const [bookedCount, setBookedCount] = useState(1);
  const bookedCountRef = useRef(1);
  const [bookedEntries, setBookedEntries] = useState([
    { guest_name: "", note: "" },
    { guest_name: "", note: "" },
  ]);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [scope, setScope] = useState("day");
  const changeBookedCount = (count) => {
    bookedCountRef.current = count;
    setBookedCount(count);
  };
  const rlist = rooms.length ? rooms : Object.keys(ROOM_LABELS).map((id) => ({ id }));
  const monthKey = `${ym.y}-${pad(ym.m + 1)}`;
  const targetIds = selectedRoom === "all" ? rlist.map((r) => r.id) : [selectedRoom];
  const rlistKey = rlist.map((r) => r.id).join("|");

  const loadCfg = useCallback(async () => {
    setCalendarLoaded(false);
    try {
      const [cfg, dailyPrices, monthCalendar] = await Promise.all([
        api.getConfig(),
        api.getDailyPrices(monthKey),
        api.getMonthCalendar(monthKey),
      ]);
      setPrices(cfg.prices || {});
      setRate(cfg.rate || 36.8);
      setDaily(dailyPrices.prices || {});
      setAvailability(monthCalendar.bookings || {});
      setAvailabilityStatuses(monthCalendar.statuses || {});
      setBookedDetails(monthCalendar.details || {});
      setBookedEntryMap(monthCalendar.booked_entries || {});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCalendarLoaded(true);
    }
  }, [monthKey]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const customPrice = (day, rid) => daily[day]?.[rid];
  const effectivePrice = (day, rid) => Number(customPrice(day, rid) ?? prices[rid] ?? 0);
  const hasCustom = (day, rid) => customPrice(day, rid) !== undefined;
  const hasAnyCustom = (day) => rlist.some((r) => hasCustom(day, r.id));
  const detailFor = (day, rid) => bookedDetails[day]?.[rid] || { guest_name: "", note: "" };
  const entriesFor = (day, rid) => bookedEntryMap[day]?.[rid] || [];
  const bookedFor = (day, rid) => availability[day]?.[rid];
  const availableFor = (day, rid) => {
    const total = ROOM_UNIT_TOTALS[rid] ?? 1;
    const booked = bookedFor(day, rid);
    return booked === undefined ? 0 : Math.max(0, total - booked);
  };

  const localizedStatus = (state) => tr(
    AVAILABILITY_STYLES[state]?.label || state,
    { open: "Abierto", closed: "Cerrado", booked: "Reservado", mixed: "Mixto" }[state] || state,
  );
  const stateFor = (day, rid) => {
    const total = ROOM_UNIT_TOTALS[rid] ?? 1;
    const booked = bookedFor(day, rid);
    // Owner policy: no explicit availability record means the night is closed.
    if (booked === undefined) return "closed";
    return availabilityStatuses[day]?.[rid] || (booked >= total ? "closed" : "open");
  };
  const roomStatus = (day, rid) => {
    const state = stateFor(day, rid);
    const total = ROOM_UNIT_TOTALS[rid] ?? 1;
    const bookedCount = bookedFor(day, rid) ?? 0;
    const style = AVAILABILITY_STYLES[state] || AVAILABILITY_STYLES.closed;
    const label = state === "booked"
      ? bookedCount >= total ? localizedStatus("booked") : `${bookedCount} ${tr("booked", "reservadas")} · ${total - bookedCount} ${tr("left", "disponibles")}`
      : localizedStatus(state);
    return {
      ...style,
      label,
      shortLabel: state === "booked" && bookedCount < total ? `${total - bookedCount} ${tr("left", "disp.")}` : localizedStatus(state),
      state,
      bookedCount,
      total,
      available: availableFor(day, rid),
      open: availableFor(day, rid) > 0,
    };
  };
  const dayStatus = (day, rid = selectedRoom) => {
    if (rid !== "all") return roomStatus(day, rid);
    const rows = rlist.map((r) => roomStatus(day, r.id));
    const first = rows[0]?.state || "closed";
    const uniform = rows.every((row) => row.state === first && (first !== "booked" || row.available === 0));
    if (uniform) {
      const style = AVAILABILITY_STYLES[first];
      return { ...style, label: localizedStatus(first), state: first, available: rlist.length, open: first === "open" };
    }
    return { ...AVAILABILITY_STYLES.mixed, label: localizedStatus("mixed"), state: "mixed", available: rows.filter((s) => s.open).length, open: true };
  };
  const cordoba = (usdValue) => `C$${Math.round(Number(usdValue || 0) * Number(rate || 0)).toLocaleString()}`;
  const usd = (value) => `$${Number(value || 0).toLocaleString()}`;

  useEffect(() => {
    const values = targetIds.map((rid) => effectivePrice(selectedDay, rid));
    const allSame = values.every((v) => v === values[0]);
    setPriceValue(allSame ? values[0] : Math.round(values.reduce((a, b) => a + b, 0) / values.length));
    setPriceMode("keep");
    const states = targetIds.map((rid) => stateFor(selectedDay, rid));
    setStatusChoice(states.every((state) => state === states[0]) ? states[0] : "");
    const bookedRows = targetIds.filter((rid) => stateFor(selectedDay, rid) === "booked");
    const counts = bookedRows.map((rid) => bookedFor(selectedDay, rid) ?? 0);
    changeBookedCount(counts.length && counts.every((count) => count === counts[0]) ? counts[0] : 0);
    const entryLists = bookedRows.map((rid) => entriesFor(selectedDay, rid));
    const firstEntries = entryLists[0] || [];
    const sameEntries = entryLists.length > 0 && entryLists.every((entries) => JSON.stringify(entries) === JSON.stringify(firstEntries));
    const source = sameEntries ? firstEntries : [];
    const first = source[0] || { guest_name: "", note: "" };
    const second = source[1] || { guest_name: "", note: "" };
    setBookedGuest(first.guest_name || "");
    setBookedNote(first.note || "");
    setBookedEntries([first, second]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, selectedRoom, daily, prices, availabilityStatuses, bookedDetails, bookedEntryMap, rlistKey]);

  const saveConfig = async () => {
    try {
      const cfg = await api.setConfig(prices, Number(rate));
      setPrices(cfg.prices);
      setRate(cfg.rate);
      toast.success(tr("Base prices saved", "Precios base guardados"));
    } catch (e) { toast.error(e.message); }
  };

  const move = (delta) =>
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      const first = iso(d.getFullYear(), d.getMonth(), 1);
      setSelectedDay(first < todayISO ? todayISO : first);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  const canGoPrev = ym.y > now.getFullYear() || (ym.y === now.getFullYear() && ym.m > now.getMonth());

  const rangeForDate = (day, mode) => {
    const selected = new Date(`${day}T12:00:00`);
    let start = new Date(selected);
    let end = new Date(selected);
    if (mode === "week") {
      start.setDate(selected.getDate() - selected.getDay());
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else if (mode === "month") {
      start = new Date(selected.getFullYear(), selected.getMonth(), 1);
      end = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
    }
    const startISO = iso(start.getFullYear(), start.getMonth(), start.getDate());
    const endISO = iso(end.getFullYear(), end.getMonth(), end.getDate());
    const nights = Math.round((end - start) / 86400000) + 1;
    return { startISO, endISO, nights };
  };

  const scopeRange = () => rangeForDate(selectedDay, scope);

  const adjustPrice = (delta) => {
    setPriceValue((value) => Math.max(0, Math.min(300, Number(value || 0) + delta)));
    setPriceMode("set");
  };

  const updateBookedEntry = (index, field, value) => {
    setBookedEntries((entries) => entries.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
    if (index === 0 && field === "guest_name") setBookedGuest(value);
    if (index === 0 && field === "note") setBookedNote(value);
  };

  const updateSelection = async () => {
    const { startISO, endISO, nights } = scopeRange();
    const target = selectedRoom === "all" ? "all" : [selectedRoom];
    const jobs = [];
    if (priceMode === "set") jobs.push(api.setDailyPriceRange(target, startISO, endISO, Number(priceValue)));
    if (priceMode === "base") jobs.push(api.setDailyPriceRange(target, startISO, endISO, null));
    const latestBookedCount = bookedCountRef.current;
    const effectiveStatus = statusChoice === "booked" && latestBookedCount === 0 ? "open" : statusChoice;
    if (effectiveStatus) {
      const bookedPayload = bookedEntries.slice(0, latestBookedCount);
      jobs.push(api.setCalendarRange(
        target,
        startISO,
        endISO,
        effectiveStatus === "closed" ? "close" : effectiveStatus,
        undefined,
        effectiveStatus === "booked" ? {
          booked_count: latestBookedCount,
          booked_entries: bookedPayload,
          guest_name: bookedPayload[0]?.guest_name || "",
          note: bookedPayload[0]?.note || "",
        } : {},
      ));
    }
    if (!jobs.length) {
      toast.error(tr("Move the price slider or choose Open, Closed, or Booked first", "Mueve el precio o elige Abierto, Cerrado o Reservado primero"));
      return;
    }
    setUpdating(true);
    try {
      await Promise.all(jobs);
      toast.success(tr(`Updated ${nights} night${nights === 1 ? "" : "s"}`, `${nights} ${nights === 1 ? "noche actualizada" : "noches actualizadas"}`));
      await loadCfg();
      await onAvailabilityChange?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUpdating(false);
    }
  };

  const popupRange = (popupScope) => rangeForDate(selectedDay, popupScope);

  const savePopupPrice = async (roomId, price, popupScope) => {
    const { startISO, endISO, nights } = popupRange(popupScope);
    const target = roomId === "all" ? "all" : [roomId];
    setUpdating(true);
    try {
      await api.setDailyPriceRange(target, startISO, endISO, Number(price));
      toast.success(tr(`Updated ${nights} night${nights === 1 ? "" : "s"}`, `${nights} ${nights === 1 ? "noche actualizada" : "noches actualizadas"}`));
      await loadCfg();
      await onAvailabilityChange?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUpdating(false);
    }
  };

  const resetPopupPrice = async (roomId, popupScope) => {
    const { startISO, endISO } = popupRange(popupScope);
    const target = roomId === "all" ? "all" : [roomId];
    setUpdating(true);
    try {
      await api.setDailyPriceRange(target, startISO, endISO, null);
      toast.success(tr("Base price restored", "Precio base restaurado"));
      await loadCfg();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUpdating(false);
    }
  };

  const setPopupStatus = async (roomId, status, popupScope, countOverride = null) => {
    const { startISO, endISO, nights } = popupRange(popupScope);
    const targetIds = roomId === "all" ? rlist.map((room) => room.id) : [roomId];
    const action = status === "closed" ? "close" : status;
    setUpdating(true);
    try {
      await Promise.all(targetIds.map((rid) => {
        const current = roomStatus(selectedDay, rid);
        let details = {};
        if (status === "booked") {
          const existing = entriesFor(selectedDay, rid);
          const count = countOverride ?? (current.state === "booked" ? Math.max(1, current.bookedCount) : current.total);
          const entries = Array.from({ length: count }, (_, i) => existing[i] || { guest_name: "", note: "" });
          details = {
            booked_count: count,
            booked_entries: entries,
            guest_name: entries[0]?.guest_name || "",
            note: entries[0]?.note || "",
          };
        }
        return api.setCalendarRange([rid], startISO, endISO, action, undefined, details);
      }));
      toast.success(tr(`Updated ${nights} night${nights === 1 ? "" : "s"}`, `${nights} ${nights === 1 ? "noche actualizada" : "noches actualizadas"}`));
      await loadCfg();
      await onAvailabilityChange?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUpdating(false);
    }
  };

  const startPad = new Date(ym.y, ym.m, 1).getDay();
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const inp = "rounded-lg border border-white/15 bg-white/5 px-2 py-2 text-sm text-white";
  const activeScope = scopeRange();
  const currentStatus = dayStatus(selectedDay, selectedRoom);
  const bookedCapacity = selectedRoom === "all" ? 2 : (ROOM_UNIT_TOTALS[selectedRoom] ?? 1);
  const currentPrices = targetIds.map((rid) => effectivePrice(selectedDay, rid));
  const minPrice = Math.min(...currentPrices);
  const maxPrice = Math.max(...currentPrices);
  // Missing availability records are closed by owner policy, so untouched months count as closed days.
  const occupancyCounts = { open: 0, booked: 0, closed: 0, mixed: 0 };
  cells.filter(Boolean).forEach((d) => {
    const state = dayStatus(iso(ym.y, ym.m, d), "all").state;
    occupancyCounts[state] += 1;
  });

  return (
    <section data-testid="admin-command-calendar">
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 shadow-2xl shadow-black/20 sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl font-bold">
              <Calendar className="h-5 w-5 text-marigold" /> {tr("Booking & price calendar", "Calendario de reservas y precios")}
            </h2>
            <p className="mt-1 text-xs text-white/50">{tr("Choose a room, price, date scope, and availability — then update in one tap.", "Elige habitación, precio, alcance de fechas y disponibilidad; luego actualiza con un toque.")}</p>
          </div>
          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <button onClick={() => move(-1)} disabled={!canGoPrev} data-testid="command-cal-prev" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-35">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <p className="min-w-32 text-center font-semibold" data-testid="command-cal-month">{tr(MONTHS[ym.m], MONTHS_ES[ym.m])} {ym.y}</p>
            <button onClick={() => move(1)} data-testid="command-cal-next" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mb-4 overflow-x-auto pb-1" data-testid="command-room-slider">
          <div className="flex min-w-max gap-2">
            <button
              onClick={() => setSelectedRoom("all")}
              data-testid="command-room-all"
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${selectedRoom === "all" ? "bg-marigold text-purpleink-deep" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
            >
              {tr("All rooms", "Todas las habitaciones")}
            </button>
            {rlist.map((room) => (
              <button
                key={room.id}
                onClick={() => setSelectedRoom(room.id)}
                data-testid={`command-room-${room.id}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${selectedRoom === room.id ? "bg-marigold text-purpleink-deep" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
              >
                {adminRoomLabel(tr, room.id)}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4 grid gap-3 lg:grid-cols-[1.25fr_0.8fr_0.8fr]">
          <div className="rounded-2xl border border-white/10 bg-black/15 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/50">{tr("Nightly price", "Precio por noche")}</p>
              <p className="text-sm font-bold text-marigold" data-testid="command-price-value">{usd(priceValue)} <span className="text-xs font-normal text-white/45">/ {cordoba(priceValue)}</span></p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => adjustPrice(-1)} disabled={!calendarLoaded} data-testid="command-price-minus" aria-label="Decrease price" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40">
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="range"
                min="0"
                max="300"
                step="1"
                value={priceValue}
                onChange={(e) => { setPriceValue(Number(e.target.value)); setPriceMode("set"); }}
                disabled={!calendarLoaded}
                data-testid="command-price-slider"
                className="w-full accent-marigold"
              />
              <button onClick={() => adjustPrice(1)} disabled={!calendarLoaded} data-testid="command-price-plus" aria-label="Increase price" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-1 rounded-full bg-white/5 p-1 text-xs">
              {[ ["keep", tr("Keep", "Mantener")], ["set", tr("Set", "Fijar")], ["base", tr("Base", "Base")] ].map(([value, label]) => (
                <button key={value} onClick={() => setPriceMode(value)} disabled={!calendarLoaded} data-testid={`command-price-${value}`} className={`rounded-full px-2 py-1.5 font-semibold disabled:opacity-40 ${priceMode === value ? "bg-marigold text-purpleink-deep" : "text-white/60"}`}>{label}</button>
              ))}
            </div>
            <p className="mt-2 text-[0.68rem] text-white/40" data-testid="command-current-price">
              {tr("Current", "Actual")}: {minPrice === maxPrice ? usd(minPrice) : `${usd(minPrice)}–${usd(maxPrice)}`}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/15 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">{tr("Update scope", "Alcance de actualización")}</p>
            <div className="grid grid-cols-3 gap-1 rounded-full bg-white/5 p-1 text-xs">
              {[ ["day", tr("Day", "Día")], ["week", tr("Week", "Semana")], ["month", tr("Month", "Mes")] ].map(([value, label]) => (
                <button key={value} onClick={() => setScope(value)} disabled={!calendarLoaded} data-testid={`command-scope-${value}`} className={`rounded-full px-2 py-2 font-semibold disabled:opacity-40 ${scope === value ? "bg-marigold text-purpleink-deep" : "text-white/60"}`}>{label}</button>
              ))}
            </div>
            <p className="mt-2 text-xs text-white/50" data-testid="command-scope-summary">{activeScope.startISO} → {activeScope.endISO} · {activeScope.nights} {tr("nights", "noches")}</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/15 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/50">{tr("Availability", "Disponibilidad")}</p>
              <p className={`text-xs font-bold ${currentStatus.className}`} data-testid="command-current-status">{currentStatus.label}</p>
            </div>
            <div className="grid grid-cols-3 gap-1 rounded-full bg-white/5 p-1 text-xs">
              {["open", "closed", "booked"].map((value) => (
                <button
                  key={value}
                  onClick={() => {
                    setStatusChoice(value);
                    if (value === "open") changeBookedCount(0);
                  }}
                  disabled={!calendarLoaded || updating}
                  data-testid={`command-status-${value}`}
                  className={`rounded-full px-2 py-2 font-semibold disabled:opacity-40 ${statusChoice === value ? AVAILABILITY_STYLES[value].active : "text-white/60"}`}
                >
                  {localizedStatus(value)}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <p className="mb-1 text-[0.68rem] font-semibold uppercase tracking-wide text-white/45">{tr("Bedrooms booked", "Habitaciones reservadas")}</p>
              <div className={`grid ${bookedCapacity > 1 ? "grid-cols-3" : "grid-cols-2"} gap-1 rounded-full bg-white/5 p-1 text-xs`} data-testid="booked-count-selector">
                {Array.from({ length: bookedCapacity + 1 }).map((_, count) => (
                  <button
                    key={count}
                    onClick={() => { changeBookedCount(count); setStatusChoice(count === 0 ? "open" : "booked"); }}
                    disabled={!calendarLoaded || updating}
                    data-testid={`booked-count-${count}`}
                    className={`rounded-full px-2 py-2 font-semibold disabled:opacity-40 ${bookedCount === count ? "bg-sky-400 text-purpleink-deep" : "text-white/60"}`}
                  >
                    {tr(`${count} booked`, `${count} reservadas`)}
                  </button>
                ))}
              </div>
            </div>
            {statusChoice === "booked" && bookedCount > 0 && (
              <div className="mt-3 grid gap-2" data-testid="booked-details">
                {Array.from({ length: bookedCount }).map((_, index) => (
                  <div key={index} className="rounded-xl border border-white/10 bg-white/[0.03] p-2" data-testid={`booked-entry-${index + 1}`}>
                    <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-wide text-white/40">{tr("Bedroom", "Habitación")} {index + 1}</p>
                    <input
                      value={index === 0 ? bookedGuest : bookedEntries[index]?.guest_name || ""}
                      onChange={(e) => updateBookedEntry(index, "guest_name", e.target.value)}
                      disabled={updating}
                      placeholder={tr("Guest name", "Nombre del huésped")}
                      data-testid={index === 0 ? "booked-guest-name" : `booked-guest-name-${index + 1}`}
                      className={inp}
                    />
                    <textarea
                      value={index === 0 ? bookedNote : bookedEntries[index]?.note || ""}
                      onChange={(e) => updateBookedEntry(index, "note", e.target.value)}
                      disabled={updating}
                      placeholder={tr("Private note", "Nota privada")}
                      rows={2}
                      data-testid={index === 0 ? "booked-private-note" : `booked-private-note-${index + 1}`}
                      className={`${inp} mt-2 resize-none`}
                    />
                  </div>
                ))}
                {selectedRoom === "all" && bookedCount === 2 && (
                  <p className="text-[0.68rem] text-white/40">{tr("One-bedroom rooms use Bedroom 1 only; two-bedroom room types can store both guests.", "Las habitaciones individuales usan solo Habitación 1; las habitaciones dobles pueden guardar ambos huéspedes.")}</p>
                )}
              </div>
            )}
            <button onClick={updateSelection} disabled={!calendarLoaded || updating} data-testid="command-update" className="mt-3 w-full rounded-full bg-marigold px-4 py-3 text-sm font-bold text-purpleink-deep shadow-lg shadow-marigold/20 hover:brightness-105 disabled:opacity-50">{updating ? tr("Updating…", "Actualizando…") : tr("Update calendar", "Actualizar calendario")}</button>
          </div>
        </div>

        <p className="mb-2 text-[0.68rem] text-white/40 sm:hidden" data-testid="command-calendar-scroll-hint">{tr("Tap a day, choose room/price/status, then update.", "Toca un día, elige habitación/precio/estado y actualiza.")}</p>
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-black/10 p-3 pb-4" data-testid="command-calendar-scroll">
          <div className="min-w-[300px]">
            <div className="mb-1 grid grid-cols-7 gap-1 sm:gap-1.5">
              {WD.map((w, i) => <div key={i} className="py-1 text-center text-[0.62rem] font-semibold uppercase text-white/40 sm:text-[0.68rem]">{tr(w, WD_ES[i])}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
              {cells.map((d, i) => {
                if (d === null) return <div key={i} />;
                const day = iso(ym.y, ym.m, d);
                const isPast = day < todayISO;
                const custom = hasAnyCustom(day);
                const active = selectedDay === day;
                const status = dayStatus(day);
                const allPrices = rlist.map((r) => effectivePrice(day, r.id));
                const min = Math.min(...allPrices);
                const max = Math.max(...allPrices);
                return (
                  <button
                    key={i}
                    disabled={isPast || !calendarLoaded || updating}
                    onClick={() => { setSelectedDay(day); setDayModalOpen(true); }}
                    data-testid={`command-day-${day}`}
                    className={`min-h-16 rounded-lg border p-1 text-left align-top transition-colors sm:min-h-24 sm:rounded-xl sm:p-2 ${active ? "border-marigold bg-marigold/25 ring-2 ring-marigold/40" : custom ? "border-marigold/40 bg-marigold/15 text-marigold" : "border-white/10 bg-white/5 text-white/80"} ${isPast ? "cursor-not-allowed opacity-35" : "hover:border-marigold/60 hover:bg-marigold/10"}`}
                  >
                    <span className="flex items-center justify-between gap-1">
                      <span className="text-xs font-bold sm:text-sm">{d}</span>
                      <span className={`h-2 w-2 rounded-full ${status.dot}`} />
                    </span>
                    <span data-testid={`command-day-status-${day}`} className={`mt-0.5 block text-[0.5rem] font-bold uppercase tracking-wide sm:text-[0.58rem] ${status.className}`}>{status.shortLabel || status.label}</span>
                    {selectedRoom === "all" ? (
                      <>
                        <span className="mt-1 block text-[0.55rem] font-semibold sm:hidden" data-testid={`command-day-price-${day}`}>{usd(min)}{min !== max ? "+" : ""}</span>
                        <span className="mt-1 hidden space-y-0.5 sm:block">
                          {rlist.map((r) => {
                            const rs = roomStatus(day, r.id);
                            const guest = detailFor(day, r.id).guest_name?.split(" ")[0] || "";
                            return (
                              <span key={r.id} className="flex items-center justify-between gap-1 text-[0.58rem] leading-tight">
                                <span className="flex min-w-0 items-center gap-1 text-white/45">
                                  <span data-testid={`command-day-${day}-${r.id}-status`} title={rs.label} className={`h-1.5 w-1.5 shrink-0 rounded-full ${rs.dot}`} />
                                  <span className="truncate">{ROOM_SHORT[r.id]}{rs.state === "booked" && guest ? ` · ${guest}` : ""}</span>
                                </span>
                                <span className={hasCustom(day, r.id) ? "font-bold text-marigold" : ""}>{usd(effectivePrice(day, r.id))}</span>
                              </span>
                            );
                          })}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={`mt-1 block text-sm font-bold ${hasCustom(day, selectedRoom) ? "text-marigold" : "text-emerald-200"}`} data-testid={`command-day-price-${day}`}>{usd(effectivePrice(day, selectedRoom))}</span>
                        <span className="block text-[0.58rem] text-white/40">{cordoba(effectivePrice(day, selectedRoom))}</span>
                        {status.state === "booked" && detailFor(day, selectedRoom).guest_name && (
                          <span className="mt-0.5 hidden truncate text-[0.58rem] font-semibold text-sky-200 sm:block" data-testid={`command-day-guest-${day}`}>
                            {detailFor(day, selectedRoom).guest_name.split(" ")[0]}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <details className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="command-defaults">
          <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-white/70">{tr("Base prices & exchange rate", "Precios base y tipo de cambio")}</summary>
          <div className="border-t border-white/10 p-4">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="text-sm text-white/70">{tr("Exchange rate — C$ per US$1", "Tipo de cambio — C$ por US$1")}</label>
              <input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="price-rate" className={`w-24 ${inp}`} />
            </div>
            <div className="space-y-2">
              {rlist.map((room) => (
                <div key={room.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm">{adminRoomLabel(tr, room.id)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">$</span>
                    <input type="number" min="0" step="1" value={prices[room.id] ?? ""} onChange={(e) => setPrices((p) => ({ ...p, [room.id]: e.target.value }))} data-testid={`price-${room.id}`} className={`w-20 ${inp}`} />
                    <span className="w-24 text-right text-xs text-white/50">{cordoba(prices[room.id])}</span>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={saveConfig} data-testid="price-save" className="mt-4 rounded-full bg-marigold px-4 py-2 text-sm font-semibold text-purpleink-deep hover:brightness-105">{tr("Save base prices", "Guardar precios base")}</button>
          </div>
        </details>
      </div>

      {dayModalOpen && (
        <DayDetailsModal
          key={selectedDay}
          day={selectedDay}
          rooms={rlist}
          roomStatus={roomStatus}
          entriesFor={entriesFor}
          effectivePrice={effectivePrice}
          basePrice={(roomId) => Number(prices[roomId] || 0)}
          rate={rate}
          updating={updating}
          onSavePrice={savePopupPrice}
          onResetPrice={resetPopupPrice}
          onSetStatus={setPopupStatus}
          onClose={() => setDayModalOpen(false)}
          tr={tr}
        />
      )}

      <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.04] p-4" data-testid="occupancy-overview">
        <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-lg font-bold">{tr("Occupancy overview", "Resumen de ocupación")}</h3>
            <p className="text-xs text-white/45">{tr("Calendar days in", "Días del calendario en")} {tr(MONTHS[ym.m], MONTHS_ES[ym.m])} {ym.y}</p>
          </div>
          <p className="text-xs text-white/40" data-testid="occupancy-total-days">{daysInMonth} {tr("days", "días")}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded-2xl bg-emerald-500/10 p-3" data-testid="occupancy-open-card">
            <p className="text-2xl font-bold text-emerald-200" data-testid="occupancy-open-count">{occupancyCounts.open}</p>
            <p className="text-xs text-emerald-100/70">{tr("days fully open", "días completamente abiertos")}</p>
          </div>
          <div className="rounded-2xl bg-sky-500/10 p-3" data-testid="occupancy-booked-card">
            <p className="text-2xl font-bold text-sky-200" data-testid="occupancy-booked-count">{occupancyCounts.booked}</p>
            <p className="text-xs text-sky-100/70">{tr("days fully booked", "días completamente reservados")}</p>
          </div>
          <div className="rounded-2xl bg-red-500/10 p-3" data-testid="occupancy-closed-card">
            <p className="text-2xl font-bold text-red-200" data-testid="occupancy-closed-count">{occupancyCounts.closed}</p>
            <p className="text-xs text-red-100/70">{tr("days fully closed", "días completamente cerrados")}</p>
          </div>
          <div className="rounded-2xl bg-marigold/10 p-3" data-testid="occupancy-mixed-card">
            <p className="text-2xl font-bold text-marigold" data-testid="occupancy-mixed-count">{occupancyCounts.mixed}</p>
            <p className="text-xs text-marigold/70">{tr("mixed days", "días mixtos")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Dashboard = ({ user, onLogout, lang, setLang }) => {
  const [rooms, setRooms] = useState([]);
  const tr = makeAdminTranslator(lang);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushState, setPushState] = useState("idle"); // idle | on | unsupported
  const [installEvt, setInstallEvt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([api.getAdminRooms(), api.getLeads()]);
      setRooms(r);
      setLeads(l);
    } catch (err) {
      if (err.status === 401) onLogout();
      else toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  // capture install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") setPushState("on");
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const enablePush = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") {
        setPushState("unsupported");
        toast.error(tr("Push notifications aren't supported on this browser/device", "Este navegador o dispositivo no admite notificaciones push"));
        return;
      }
      const reg = await navigator.serviceWorker.register("/admin-sw.js", { scope: "/admin" });
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error(tr("Notifications permission denied", "Permiso de notificaciones denegado"));
        return;
      }
      const { key } = await api.vapidKey();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }
      await api.subscribePush(JSON.parse(JSON.stringify(sub)));
      setPushState("on");
      toast.success(tr("Push notifications enabled", "Notificaciones push activadas"));
    } catch (err) {
      toast.error(err.message || tr("Could not enable push", "No se pudieron activar las notificaciones"));
    }
  };

  const install = async () => {
    if (!installEvt) {
      toast.info(tr("Use your browser menu → 'Add to Home Screen' to install", "Usa el menú del navegador → 'Añadir a pantalla de inicio' para instalar"));
      return;
    }
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };


  const refreshRooms = useCallback(async () => {
    try {
      setRooms(await api.getAdminRooms());
    } catch (err) {
      toast.error(err.message);
    }
  }, []);
  const markLead = async (lead) => {
    const next = lead.status === "contacted" ? "new" : "contacted";
    setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, status: next } : x)));
    try {
      await api.setLeadStatus(lead.id, next);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const availClass = (a, total) =>
    a <= 0 ? "text-red-300" : a >= total ? "text-emerald-300" : "text-marigold";

  return (
    <div className="min-h-screen bg-purpleink-deep pb-16 text-white grain" data-testid="admin-dashboard">
      <header
        data-testid="admin-topbar"
        className="fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b border-white/10 bg-purpleink-deep/95 px-4 backdrop-blur-xl sm:px-8"
        style={{ height: "calc(4rem + env(safe-area-inset-top))", paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Casa Romano" className="h-9 w-9 rounded-full object-cover" />
          <div>
            <p className="font-display text-lg font-bold leading-none">Casa Romano</p>
            <p className="hidden text-xs text-white/50 sm:block">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setLang(lang === "es" ? "en" : "es")} data-testid="admin-language-toggle" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-xs font-bold text-white/70 hover:bg-white/10" aria-label={tr("Change language", "Cambiar idioma")}>
            {lang === "es" ? "ES" : "EN"}
          </button>
          <button onClick={load} data-testid="admin-refresh" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 hover:bg-white/10" aria-label={tr("Refresh", "Actualizar")}>
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              try { sessionStorage.setItem("cr_skip_admin_redirect", "1"); } catch (e) { /* noop */ }
              window.location.href = "/";
            }}
            data-testid="admin-view-site"
            className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-2 text-sm hover:bg-white/10"
          >
            <Globe className="h-4 w-4" /> <span className="hidden sm:inline">{tr("Website", "Sitio web")}</span>
          </button>
          <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
            <LogOut className="h-4 w-4" /> {tr("Logout", "Salir")}
          </button>
        </div>
      </header>
      <div aria-hidden="true" style={{ height: "calc(4rem + env(safe-area-inset-top))" }} />

      <div className="mx-auto max-w-6xl space-y-8 px-4 pt-6 sm:px-8">
        <PricesPanel rooms={rooms} onAvailabilityChange={refreshRooms} tr={tr} />

        {/* actions */}
        <div className="flex flex-wrap gap-3">
          <button onClick={enablePush} data-testid="admin-enable-push" className="flex items-center gap-2 rounded-full bg-marigold px-4 py-2.5 text-sm font-semibold text-purpleink-deep hover:brightness-105">
            <Bell className="h-4 w-4" /> {pushState === "on" ? tr("Notifications on", "Notificaciones activadas") : tr("Enable notifications", "Activar notificaciones")}
          </button>
          <button onClick={install} data-testid="admin-install" className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold hover:bg-white/10">
            <Download className="h-4 w-4" /> {tr("Add to home screen", "Añadir a pantalla de inicio")}
          </button>
          {pushState === "on" && (
            <button onClick={() => api.testPush().then(() => toast.success(tr("Test sent", "Prueba enviada"))).catch((e) => toast.error(e.message))} className="rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm hover:bg-white/10">
              {tr("Send test push", "Enviar notificación de prueba")}
            </button>
          )}
        </div>

        {/* rooms */}
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold">
            <BedDouble className="h-5 w-5 text-marigold" /> {tr("Room availability", "Disponibilidad de habitaciones")}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {rooms.map((room) => (
              <div key={room.id} data-testid={`admin-room-${room.id}`} className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">{adminRoomLabel(tr, room.id)}</p>
                    <p className={`text-sm ${availClass(room.available, room.total)}`}>
                      {room.available === 0 ? tr("Closed tonight", "Cerrado esta noche") : `${room.available} ${tr("of", "de")} ${room.total} ${tr("available tonight", "disponibles esta noche")}`}
                    </p>
                  </div>
                  <span className={`rounded-full px-3 py-1.5 text-sm font-bold ${room.available === 0 ? "bg-red-500/15 text-red-200" : "bg-emerald-500/15 text-emerald-200"}`} data-testid={`admin-room-${room.id}-count`}>
                    {room.available}/{room.total}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* leads */}
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold">
            <MessageSquare className="h-5 w-5 text-marigold" /> {tr("Leads", "Solicitudes")}
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium">{leads.length}</span>

          </h2>
          {loading ? (
            <p className="text-white/50">{tr("Loading…", "Cargando…")}</p>
          ) : leads.length === 0 ? (
            <p className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-8 text-center text-white/50">{tr("No leads yet. They'll appear here when guests submit the reservation form.", "Aún no hay solicitudes. Aparecerán aquí cuando los huéspedes envíen el formulario de reserva.")}</p>
          ) : (
            <div className="space-y-3">
              {leads.map((lead) => (
                <div key={lead.id} data-testid={`admin-lead-${lead.id}`} className={`rounded-2xl border px-4 py-4 ${lead.status === "contacted" ? "border-white/5 bg-white/[0.02] opacity-70" : "border-white/10 bg-white/[0.04]"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold">{lead.name}{lead.room ? <span className="text-white/50"> · {lead.room}</span> : null}</p>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/60">
                        {lead.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{lead.email}</span>}
                        {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{lead.phone}</span>}
                        {(lead.checkin || lead.checkout) && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{lead.checkin || "?"} → {lead.checkout || "?"}</span>}
                      </div>
                      {lead.message && <p className="mt-2 text-sm text-white/75">{lead.message}</p>}
                      <p className="mt-2 text-xs text-white/35">{new Date(lead.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => markLead(lead)} data-testid={`admin-lead-${lead.id}-toggle`} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${lead.status === "contacted" ? "bg-white/10 text-white/70" : "bg-marigold text-purpleink-deep"}`}>
                      {lead.status === "contacted" ? tr("Contacted", "Contactado") : tr("Mark contacted", "Marcar contactado")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [adminLang, setAdminLang] = useState(() => localStorage.getItem("cr_admin_lang") || "es");

  useEffect(() => {
    localStorage.setItem("cr_admin_lang", adminLang);
  }, [adminLang]);

  useEffect(() => {
    // swap manifest to admin manifest for installability scoped to /admin
    let link = document.querySelector("link[rel='manifest']");
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/admin-manifest.json");
    // Keep the admin dashboard out of search engines (private owner tool).
    const noindex = document.createElement("meta");
    noindex.setAttribute("name", "robots");
    noindex.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(noindex);
    // Recover any older service worker that intercepted fetches (caused login flicker):
    // update existing registrations so the new no-fetch-handler SW takes over.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.update()))
        .catch(() => {});
    }
    return () => {
      if (link && prev) link.setAttribute("href", prev);
      noindex.remove();
    };
  }, []);

  useEffect(() => {
    if (!tokenStore.get()) {
      setChecking(false);
      return;
    }
    // Restore session immediately from cache so a valid login never flickers back to login.
    let hasCache = false;
    try {
      const cached = localStorage.getItem("cr_admin_user");
      if (cached) {
        setUser(JSON.parse(cached));
        hasCache = true;
      }
    } catch (e) {
      /* ignore bad cache */
    }
    // If we have a cached user, show it right away and verify in the background.
    // If not, stay on the loading screen (never flash the login form) until api.me() resolves.
    if (hasCache) setChecking(false);
    api
      .me()
      .then((u) => {
        localStorage.setItem("cr_admin_user", JSON.stringify(u));
        setUser(u);
      })
      .catch((err) => {
        if (err.status === 401) {
          tokenStore.clear();
          localStorage.removeItem("cr_admin_user");
          setUser(null);
        }
      })
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = (u) => {
    localStorage.setItem("cr_admin_user", JSON.stringify(u));
    setUser(u);
  };

  const logout = () => {
    tokenStore.clear();
    localStorage.removeItem("cr_admin_user");
    // Let the installed-app launch redirect know to skip sending us back to /admin
    // this one time, so logout lands on the public website.
    try {
      sessionStorage.setItem("cr_skip_admin_redirect", "1");
    } catch (e) {
      /* noop */
    }
    setUser(null);
    window.location.href = "/";
  };


  const tr = makeAdminTranslator(adminLang);
  if (checking) return <div className="flex min-h-screen items-center justify-center bg-purpleink-deep text-white/60">{tr("Loading…", "Cargando…")}</div>;
  return (
    <>
      {!user ? <Login onLogin={handleLogin} lang={adminLang} setLang={setAdminLang} /> : <Dashboard user={user} onLogout={logout} lang={adminLang} setLang={setAdminLang} />}
      <Toaster position="top-center" richColors />
    </>
  );
}
